import { useState, useCallback } from 'react';
import { ApiResponse, ErrorResponse } from '../types';

interface UseApiCallState<T> {
  data: T | null;
  loading: boolean;
  error: ErrorResponse | null;
}

interface UseApiCallReturn<T> extends UseApiCallState<T> {
  execute: () => Promise<void>;
  reset: () => void;
}

export function useApiCall<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: ErrorResponse) => void;
    initialData?: T;
  }
): UseApiCallReturn<T> {
  const [state, setState] = useState<UseApiCallState<T>>({
    data: options?.initialData || null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiCall();

      if (response.error) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: response.error!,
        }));
        
        if (options?.onError) {
          options.onError(response.error);
        }
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          data: response.data!,
        }));
        
        if (options?.onSuccess) {
          options.onSuccess(response.data!);
        }
      }
    } catch (error) {
      const errorResponse: ErrorResponse = {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
        },
        timestamp: new Date().toISOString(),
        requestId: 'client-error',
      };

      setState(prev => ({
        ...prev,
        loading: false,
        error: errorResponse,
      }));

      if (options?.onError) {
        options.onError(errorResponse);
      }
    }
  }, [apiCall, options]);

  const reset = useCallback(() => {
    setState({
      data: options?.initialData || null,
      loading: false,
      error: null,
    });
  }, [options?.initialData]);

  return {
    ...state,
    execute,
    reset,
  };
}

export default useApiCall;